// Argus — FileExplorerWindow: panou principal File Explorer (Faza 4).
// Layout: Breadcrumb top + DirectoryContent (sortabil + filter + view modes) + status bar.
// Sync flow: GET cache imediat + emit dir_listing background → invalideaza la success.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Typography, Alert, ToggleButton, ToggleButtonGroup, Tooltip, Divider } from '@mui/material';
import { formatBytes } from '../lib/dirListing';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import {
  fetchFsEntries,
  refreshFsEntriesViaWs,
  joinPath,
  parentOfPath,
  PathNotFoundError,
  type FsEntry,
} from '../lib/fsEntries';
import {
  WsAgentOfflineError,
  WsDisconnectedError,
  WsTimeoutError,
} from '../lib/wsClient';
import { readFilePreview, previewKindFromName } from '../lib/fileRead';
import { createMseVideoSource, type MseVideoSource } from '../lib/videoStream';
import { Breadcrumb } from './file-explorer/Breadcrumb';
import { DirectoryContent, loadViewMode, saveViewMode, type ViewMode, type SortKey, type SortDir } from './file-explorer/DirectoryContent';
import { FilePreviewPanel } from './file-explorer/FilePreviewPanel';
import { DirectoryContextMenu } from './file-explorer/DirectoryContextMenu';
import { PropertiesDialog } from './file-explorer/PropertiesDialog';
import { FolderTreeView } from './file-explorer/FolderTreeView';
import { PreviewPane } from './file-explorer/PreviewPane';
import { ToolbarMenu } from './file-explorer/ToolbarMenu';
import { FileExplorerOptionsDialog, type FileExplorerOptions } from './file-explorer/FileExplorerOptionsDialog';
import { useNavigationHistory } from './file-explorer/useNavigationHistory';
import { DownloadAllPanel } from './file-explorer/DownloadAllPanel';
import {
  bulkDownload,
  buildDownloadItems,
  BulkDownloadCancelled,
  BulkDownloadUnsupportedBrowser,
  type BulkDownloadProgress,
} from '../lib/bulkDownload';
import {
  enumerateDataTransferEntries,
  fileListToDropEntries,
  pushFiles,
  type PushItem,
} from '../lib/filePush';
import { FilePushProgressPanel } from './file-explorer/FilePushProgressPanel';

export interface FileExplorerWindowProps {
  agentId: string;
  initialPath?: string;
  agentOnline?: boolean;
}

const PATH_STORAGE_PREFIX = 'argus.fileExplorer.lastPath.';
const PREVIEW_OPEN_PREFIX = 'argus.fileExplorer.previewOpen.';
const TREE_OPEN_PREFIX = 'argus.fileExplorer.treeOpen.';
const SHOW_SYSTEM_PREFIX = 'argus.fileExplorer.showSystem.';
const SHOW_HIDDEN_PREFIX = 'argus.fileExplorer.showHidden.';
const REAL_THUMBS_PREFIX = 'argus.fileExplorer.realThumbs.';

function loadTreeOpen(agentId: string): boolean {
  if (typeof window === 'undefined') return true;
  const stored = localStorage.getItem(TREE_OPEN_PREFIX + agentId);
  return stored === null ? true : stored === '1';
}

function saveTreeOpen(agentId: string, open: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(TREE_OPEN_PREFIX + agentId, open ? '1' : '0');
  }
}

function formatSelectedSize(entry: FsEntry): string {
  if (entry.driveType !== undefined && entry.driveType !== null) {
    const free = entry.freeBytes ?? 0;
    const total = entry.totalBytes ?? 0;
    if (total > 0) return `${formatBytes(free)} free of ${formatBytes(total)}`;
    return 'Drive';
  }
  if (entry.isDir) return 'Folder';
  if (entry.size !== null && entry.size !== undefined) return formatBytes(entry.size);
  return '—';
}

function loadLastPath(agentId: string): string {
  if (typeof window === 'undefined') return '';
  const stored = localStorage.getItem(PATH_STORAGE_PREFIX + agentId);
  if (stored !== null) return stored;
  return ''; // empty = drives root (windows-style My Computer)
}

function saveLastPath(agentId: string, path: string): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(PATH_STORAGE_PREFIX + agentId, path);
  }
}

function loadPreviewOpen(agentId: string): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(PREVIEW_OPEN_PREFIX + agentId) === '1';
}

function savePreviewOpen(agentId: string, open: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(PREVIEW_OPEN_PREFIX + agentId, open ? '1' : '0');
  }
}

function loadBoolPref(prefix: string, agentId: string, defaultValue: boolean): boolean {
  if (typeof window === 'undefined') return defaultValue;
  const stored = localStorage.getItem(prefix + agentId);
  if (stored === null) return defaultValue;
  return stored === '1';
}

function saveBoolPref(prefix: string, agentId: string, value: boolean): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(prefix + agentId, value ? '1' : '0');
  }
}

export function FileExplorerWindow({ agentId, initialPath, agentOnline = true }: FileExplorerWindowProps): JSX.Element {
  const nav = useNavigationHistory(initialPath ?? loadLastPath(agentId));
  const currentPath = nav.currentPath;
  const setCurrentPath = nav.navigate;
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewName, setPreviewName] = useState<string>('');
  const [previewPath, setPreviewPath] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewDownloadUrl, setPreviewDownloadUrl] = useState<string | null>(null);
  const [contextMenuEntry, setContextMenuEntry] = useState<FsEntry | null>(null);
  const [contextMenuPos, setContextMenuPos] = useState<{ top: number; left: number } | null>(null);
  // Bloc file-explorer-multiselect-download 2026-05-06: state lifted multi-select.
  const [selectedNames, setSelectedNames] = useState<ReadonlySet<string>>(() => new Set());
  const [lastClickedName, setLastClickedName] = useState<string | null>(null);
  const [orderedNames, setOrderedNames] = useState<readonly string[]>([]);
  // Reset multi-select la schimbare path.
  useEffect(() => {
    setSelectedNames(new Set());
    setLastClickedName(null);
  }, [currentPath]);
  // Bulk download state.
  const [bulkActive, setBulkActive] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<BulkDownloadProgress | null>(null);
  const bulkAbortRef = useRef<AbortController | null>(null);
  // Bloc file-push-deployment 2026-05-07 — DnD upload state.
  const [pushItems, setPushItems] = useState<PushItem[]>([]);
  const [pushVisible, setPushVisible] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const pushAbortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();
  // Bloc v1.0.10 #C — propertiesTarget unifică ambele flow-uri (right-click pe entry + Options→Properties pentru currentPath).
  // Pentru sub-element: { entry, fullPath: joinPath(currentPath, entry.name) }; pentru currentPath: { entry sintetic, fullPath: currentPath }.
  const [propertiesEntry, setPropertiesEntry] = useState<FsEntry | null>(null);
  const [propertiesFullPath, setPropertiesFullPath] = useState<string>('');
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());
  const [previewPaneOpen, setPreviewPaneOpen] = useState<boolean>(() => loadPreviewOpen(agentId));
  const [treeOpen, setTreeOpen] = useState<boolean>(() => loadTreeOpen(agentId));
  const [showSystemFiles, setShowSystemFiles] = useState<boolean>(() => loadBoolPref(SHOW_SYSTEM_PREFIX, agentId, false));
  const [showHiddenFiles, setShowHiddenFiles] = useState<boolean>(() => loadBoolPref(SHOW_HIDDEN_PREFIX, agentId, false));
  const [realMediaThumbnails, setRealMediaThumbnails] = useState<boolean>(() => loadBoolPref(REAL_THUMBS_PREFIX, agentId, true));
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  useEffect(() => { saveViewMode(viewMode); }, [viewMode]);
  useEffect(() => { savePreviewOpen(agentId, previewPaneOpen); }, [agentId, previewPaneOpen]);
  useEffect(() => { saveTreeOpen(agentId, treeOpen); }, [agentId, treeOpen]);
  useEffect(() => { saveBoolPref(SHOW_SYSTEM_PREFIX, agentId, showSystemFiles); }, [agentId, showSystemFiles]);
  useEffect(() => { saveBoolPref(SHOW_HIDDEN_PREFIX, agentId, showHiddenFiles); }, [agentId, showHiddenFiles]);
  useEffect(() => { saveBoolPref(REAL_THUMBS_PREFIX, agentId, realMediaThumbnails); }, [agentId, realMediaThumbnails]);

  useEffect(() => {
    saveLastPath(agentId, currentPath);
  }, [agentId, currentPath]);

  const queryKey = ['fsEntries', agentId, currentPath, agentOnline] as const;

  // Single source: WS direct când agent online; REST DB cache când offline.
  const { data, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (agentOnline) {
        try {
          const items = await refreshFsEntriesViaWs(agentId, currentPath);
          return { parentPath: currentPath, items };
        } catch (err) {
          if (err instanceof WsAgentOfflineError || err instanceof WsDisconnectedError || err instanceof WsTimeoutError) {
            // Fallback la cache DB (entries from last successful WS request).
            return await fetchFsEntries(agentId, currentPath);
          }
          throw err;
        }
      }
      return await fetchFsEntries(agentId, currentPath);
    },
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  // Auto-handle PathNotFoundError → navigate up.
  useEffect(() => {
    if (!agentOnline) return;
    setRefreshing(isLoading);
    return () => setRefreshing(false);
  }, [agentOnline, isLoading]);

  // Bloc file-push-deployment 2026-05-07 — listen `argus:file-pushed` broadcast → refresh fsEntries.
  useEffect(() => {
    function onFilePushed(ev: Event): void {
      const ce = ev as CustomEvent<{ agentId: string; parentPath: string }>;
      if (!ce.detail) return;
      if (ce.detail.agentId.toUpperCase() !== agentId.toUpperCase()) return;
      // Refresh DOAR dacă parentPath e folderul afișat curent (NU descendents deep).
      const matchesCurrent = (ce.detail.parentPath || '').replace(/\\$/, '').toLowerCase()
        === (currentPath || '').replace(/\\$/, '').toLowerCase();
      if (matchesCurrent) {
        void queryClient.invalidateQueries({ queryKey: ['fsEntries', agentId, currentPath] });
      }
    }
    window.addEventListener('argus:file-pushed', onFilePushed);
    return () => window.removeEventListener('argus:file-pushed', onFilePushed);
  }, [agentId, currentPath, queryClient]);

  // Bloc file-push-deployment — DnD handlers pe content pane Box.
  const handleDragEnter = useCallback((e: React.DragEvent): void => {
    if (!agentOnline) return;
    if (e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      dragDepthRef.current++;
      setIsDragOver(true);
    }
  }, [agentOnline]);

  const handleDragOver = useCallback((e: React.DragEvent): void => {
    if (!agentOnline) return;
    if (e.dataTransfer.types && Array.from(e.dataTransfer.types).includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [agentOnline]);

  const handleDragLeave = useCallback((e: React.DragEvent): void => {
    e.preventDefault();
    dragDepthRef.current--;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent): Promise<void> => {
    if (!agentOnline) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);

    if (currentPath === '') {
      // Drop pe drives root — refuz, nu avem path absolut clar pentru a scrie.
      setRefreshError('Cannot drop here. Navigate into a drive first.');
      return;
    }

    let drops;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      try {
        drops = await enumerateDataTransferEntries(e.dataTransfer.items);
      } catch (err) {
        // Fallback la flat FileList.
        drops = fileListToDropEntries(e.dataTransfer.files);
      }
    } else {
      drops = fileListToDropEntries(e.dataTransfer.files);
    }
    if (drops.length === 0) return;

    pushAbortRef.current?.abort();
    const abort = new AbortController();
    pushAbortRef.current = abort;
    setPushItems([]);
    setPushVisible(true);
    try {
      await pushFiles(agentId, currentPath, drops, (items) => setPushItems([...items]), abort.signal);
    } catch (err) {
      // pushFiles itself nu throw — erorile sunt per-item în status='failed'.
      setRefreshError(err instanceof Error ? err.message : 'Push failed');
    }
  }, [agentId, agentOnline, currentPath]);

  const handlePushCancel = useCallback((): void => {
    pushAbortRef.current?.abort();
  }, []);

  const handlePushClose = useCallback((): void => {
    setPushVisible(false);
    setPushItems([]);
  }, []);

  // Auto-close progress panel după ce TOATE items s-au terminat (done/failed/cancelled).
  // Pe success-only → 2s delay (utilizatorul vede check verzi). Cu failures/cancels → keep open
  // ca user să vadă error-urile. User poate închide manual oricând (X button enabled post-allDone).
  useEffect(() => {
    if (!pushVisible || pushItems.length === 0) return;
    const allDone = pushItems.every((it) => it.status === 'done' || it.status === 'failed' || it.status === 'cancelled');
    const allSuccess = pushItems.every((it) => it.status === 'done');
    if (allDone && allSuccess) {
      const t = setTimeout(() => {
        setPushVisible(false);
        setPushItems([]);
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [pushItems, pushVisible]);

  const handleManualRefresh = async () => {
    if (!agentOnline) return;
    setRefreshing(true);
    setRefreshError(null);
    try {
      await refetch({ throwOnError: true });
    } catch (err) {
      if (err instanceof PathNotFoundError && currentPath !== '') {
        const parent = parentOfPath(currentPath);
        setRefreshError(`Folder no longer exists. Navigated to parent.`);
        setCurrentPath(parent);
      } else {
        setRefreshError(err instanceof Error ? err.message : 'Refresh failed');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleOpenDir = (name: string) => {
    // Pass `name` as selectedForCurrent so Back restores selection on the parent folder.
    nav.navigate(joinPath(currentPath, name), name);
  };

  // Bloc ws-direct-batch-3: track MSE source pentru cleanup la close.
  const previewMseRef = useRef<MseVideoSource | null>(null);
  const handleOpenFile = async (entry: FsEntry) => {
    if (!agentOnline) return;
    const filePath = joinPath(currentPath, entry.name);
    // Cleanup previous MSE source dacă deschidem alt file.
    if (previewMseRef.current) {
      previewMseRef.current.dispose();
      previewMseRef.current = null;
    }
    setPreviewOpen(true);
    setPreviewName(entry.name);
    setPreviewPath(filePath);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewDownloadUrl(null);
    try {
      const kind = previewKindFromName(entry.name);
      const previewKind = kind === 'text' || kind === 'image' || kind === 'video' ? kind : 'binary';
      const result = await readFilePreview(agentId, filePath, '', previewKind);
      setPreviewDownloadUrl(result.downloadUrl || null);
    } catch (err) {
      // Bloc ws-direct-batch-3: video >10 MB → fallback la MSE streaming.
      // Duck-typing pe errorCode (instanceof poate eșua cross-HMR module instances).
      const isTooLarge = err !== null && err !== undefined && typeof err === 'object'
        && (err as { errorCode?: string }).errorCode === 'FILE_TOO_LARGE_FOR_WS';
      if (isTooLarge && previewKindFromName(entry.name) === 'video') {
        console.debug('[FileExplorerWindow] FILE_TOO_LARGE_FOR_WS for video → fallback MSE streaming');
        try {
          const mse = createMseVideoSource(agentId, filePath, '', {
            onError: (mseError) => {
              console.error('[FileExplorerWindow] MSE error:', mseError);
              setPreviewError(`Streaming eșuat: ${mseError.message}`);
            },
          });
          previewMseRef.current = mse;
          setPreviewDownloadUrl(mse.srcUrl);
        } catch (mseErr) {
          setPreviewError(`Streaming eșuat: ${mseErr instanceof Error ? mseErr.message : String(mseErr)}`);
        }
      } else {
        setPreviewError(err instanceof Error ? err.message : 'file_read failed');
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  // Cleanup MSE la close modal.
  useEffect(() => {
    if (!previewOpen && previewMseRef.current) {
      previewMseRef.current.dispose();
      previewMseRef.current = null;
    }
  }, [previewOpen]);

  const rawEntries = data?.items ?? [];

  const entries = useMemo(() => {
    return rawEntries.filter((e) => {
      if (!showSystemFiles && e.isSystem === true) return false;
      if (!showHiddenFiles && e.isHidden === true) return false;
      return true;
    });
  }, [rawEntries, showSystemFiles, showHiddenFiles]);

  const selectedFsEntry = useMemo<FsEntry | null>(() => {
    if (nav.selectedItem === null) return null;
    return entries.find((e) => e.name === nav.selectedItem) ?? null;
  }, [entries, nav.selectedItem]);

  // Bloc file-explorer-multiselect-download 2026-05-06: handlers multi-select.
  const handleMultiClick = useCallback((entryName: string, modifiers: { ctrlKey: boolean; shiftKey: boolean }): void => {
    if (modifiers.shiftKey) {
      const anchor = lastClickedName ?? orderedNames[0] ?? null;
      if (anchor === null) {
        setSelectedNames(new Set([entryName]));
        setLastClickedName(entryName);
        return;
      }
      const ai = orderedNames.indexOf(anchor);
      const ti = orderedNames.indexOf(entryName);
      if (ai === -1 || ti === -1) {
        setSelectedNames(new Set([entryName]));
        setLastClickedName(entryName);
        return;
      }
      const [from, to] = ai < ti ? [ai, ti] : [ti, ai];
      setSelectedNames(new Set(orderedNames.slice(from, to + 1)));
      // Anchor preserved.
      return;
    }
    if (modifiers.ctrlKey) {
      const next = new Set(selectedNames);
      if (next.has(entryName)) next.delete(entryName);
      else next.add(entryName);
      setSelectedNames(next);
      setLastClickedName(entryName);
      return;
    }
    setSelectedNames(new Set([entryName]));
    setLastClickedName(entryName);
  }, [selectedNames, lastClickedName, orderedNames]);

  const handleMultiSetSelection = useCallback((next: ReadonlySet<string>, _modifiers?: { ctrlKey: boolean; shiftKey: boolean }): void => {
    // Hook-ul useDragRectangle deja calculează merge cu modifiers + initial snapshot.
    setSelectedNames(next);
    // Sync nav.selectedItem (single-select used by PreviewPane) cu multi-select.
    // Treat ca selecție standard unificată: 1 item → preview; 0 sau >1 → clear preview.
    if (next.size === 1) {
      const only = next.values().next().value as string;
      nav.setSelectedItem(only);
    } else {
      nav.setSelectedItem(null);
    }
    if (next.size === 0) setLastClickedName(null);
  }, [nav]);

  const handleDownloadAll = useCallback(async () => {
    // Dacă selecția e goală, dar context menu invocat pe item: caller deja a făcut select pe item.
    const namesToDownload = selectedNames.size > 0
      ? Array.from(selectedNames)
      : (contextMenuEntry ? [contextMenuEntry.name] : []);
    if (namesToDownload.length === 0) return;
    const items = buildDownloadItems(new Set(namesToDownload), entries, currentPath);
    if (items.length === 0) return;
    const ac = new AbortController();
    bulkAbortRef.current = ac;
    setBulkActive(true);
    try {
      await bulkDownload({
        agentId,
        basePath: currentPath,
        items,
        onProgress: (p) => setBulkProgress(p),
        abortSignal: ac.signal,
      });
    } catch (err) {
      if (err instanceof BulkDownloadUnsupportedBrowser) {
        setRefreshError('Browser nesuportat. Folosește Chrome/Edge ≥ 86 pentru download cu structură directoare.');
        setBulkActive(false);
        return;
      }
      if (err instanceof BulkDownloadCancelled) {
        setBulkActive(false);
        return;
      }
      setRefreshError(err instanceof Error ? err.message : 'Bulk download failed');
    }
  }, [selectedNames, contextMenuEntry, entries, currentPath, agentId]);

  const handleBulkCancel = useCallback(() => {
    bulkAbortRef.current?.abort();
  }, []);

  const handleBulkClose = useCallback(() => {
    setBulkActive(false);
    setBulkProgress(null);
    bulkAbortRef.current = null;
  }, []);

  const optionsValue: FileExplorerOptions = {
    treeOpen,
    previewOpen: previewPaneOpen,
    showSystemFiles,
    showHiddenFiles,
    realMediaThumbnails,
  };

  const handleOptionsChange = (next: FileExplorerOptions): void => {
    setTreeOpen(next.treeOpen);
    setPreviewPaneOpen(next.previewOpen);
    setShowSystemFiles(next.showSystemFiles);
    setShowHiddenFiles(next.showHiddenFiles);
    setRealMediaThumbnails(next.realMediaThumbnails);
  };

  // Bloc v1.0.10 #C — unify Properties: Options menu Properties + right-click Properties folosesc
  // ACELAȘI propertiesEntry render path. Sintetizăm un FsEntry pentru currentPath dacă nu există în
  // entries[] (e.g. drive root listing din parent). Fallback minimal entry cu name + isDir.
  const handleOpenPropertiesCurrent = (): void => {
    const isDriveRoot = /^[A-Za-z]:\\?$/.test(currentPath);
    const folderName = currentPath === '' ? 'This PC' : (currentPath.split('\\').filter(Boolean).pop() ?? currentPath);
    // Build synthetic entry pentru folder/drive curent (no enriched metadata available local).
    const synthetic = {
      name: folderName,
      isDir: true,
      size: null,
      modifiedAt: null,
      isHidden: false,
      isSystem: false,
      iconHash: null,
      driveType: isDriveRoot ? 'Local' : null,
      freeBytes: null,
      totalBytes: null,
      volumeLabel: null,
      lastSyncedAt: null,
    } as unknown as FsEntry;
    setPropertiesEntry(synthetic);
    setPropertiesFullPath(currentPath);
  };

  return (
    <Box data-role="file-explorer-panel" sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>
      <Breadcrumb
        currentPath={currentPath}
        canGoBack={nav.canGoBack}
        canGoForward={nav.canGoForward}
        canGoUp={nav.canGoUp}
        onBack={nav.back}
        onForward={nav.forward}
        onUp={nav.up}
        onNavigate={(p) => setCurrentPath(p)}
        onRefresh={() => void handleManualRefresh()}
        refreshing={refreshing}
        trailingMenuSlot={
          <ToolbarMenu
            treeOpen={treeOpen}
            previewOpen={previewPaneOpen}
            viewMode={viewMode}
            sortKey={sortKey}
            sortDir={sortDir}
            hasCurrentEntry
            onToggleTree={() => setTreeOpen((o) => !o)}
            onTogglePreview={() => setPreviewPaneOpen((o) => !o)}
            onChangeView={(m) => setViewMode(m)}
            onChangeSortKey={(k) => setSortKey(k)}
            onChangeSortDir={(d) => setSortDir(d)}
            onOpenOptions={() => setOptionsOpen(true)}
            onOpenProperties={handleOpenPropertiesCurrent}
          />
        }
      />

      {!agentOnline ? (
        <Alert severity="warning" variant="filled" sx={{ borderRadius: 0, py: 0.5 }}>
          Agent offline — showing cache only (read-only).
        </Alert>
      ) : null}

      {refreshError ? (
        <Alert severity="error" variant="outlined" sx={{ borderRadius: 0, py: 0.5 }} onClose={() => setRefreshError(null)}>
          Refresh failed: {refreshError}
        </Alert>
      ) : null}

      <Box data-role="file-explorer-content" sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'row', minHeight: 0 }}>
        {treeOpen ? (
          <>
            <Box sx={{ width: 240, flexShrink: 0, overflow: 'hidden', borderRight: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
              <FolderTreeView
                agentId={agentId}
                currentPath={currentPath}
                onNavigate={(p) => setCurrentPath(p)}
              />
            </Box>
          </>
        ) : null}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            position: 'relative',
            outline: isDragOver ? '2px dashed' : 'none',
            outlineColor: 'primary.main',
            outlineOffset: -4,
            transition: 'outline-color 120ms',
            backgroundColor: isDragOver ? 'action.hover' : 'transparent',
          }}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={(e) => { void handleDrop(e); }}
        >
          {pushVisible && (
            <FilePushProgressPanel
              items={pushItems}
              pcName={agentId.slice(0, 8) + '…'}
              onCancel={handlePushCancel}
              onClose={handlePushClose}
            />
          )}
          <DirectoryContent
            entries={entries}
            isLoading={isLoading || refreshing}
            isDrivesRoot={currentPath === ''}
            agentId={agentId}
            parentPath={currentPath}
            viewMode={viewMode}
            selectedEntryName={nav.selectedItem}
            onSelect={(entry) => nav.setSelectedItem(entry?.name ?? null)}
            onOpenDir={handleOpenDir}
            onOpenFile={(entry) => void handleOpenFile(entry)}
            onContextMenu={(entry, pos) => {
              // Fix #6: select on right-click ÎNAINTE de open menu.
              nav.setSelectedItem(entry.name);
              // Bloc file-explorer-multiselect-download: dacă right-click pe item NESELECTAT,
              // update multi-selection la single (Pattern Windows Explorer).
              if (!selectedNames.has(entry.name)) {
                setSelectedNames(new Set([entry.name]));
                setLastClickedName(entry.name);
              }
              // Fix #7: replace any existing menu cu noul entry + pos (set state-overwrite — Menu re-positions).
              setContextMenuEntry(entry);
              setContextMenuPos(pos);
            }}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={(k, d) => { setSortKey(k); setSortDir(d); }}
            realMediaThumbnails={realMediaThumbnails}
            selectedNames={selectedNames}
            onMultiClick={handleMultiClick}
            onMultiSetSelection={handleMultiSetSelection}
            onOrderedEntriesChange={setOrderedNames}
          />
        </Box>
        {previewPaneOpen ? (
          <>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ width: 320, flexShrink: 0, overflow: 'hidden' }}>
              <PreviewPane
                agentId={agentId}
                agentOnline={agentOnline}
                parentPath={currentPath}
                selectedEntry={selectedFsEntry}
                onExpandPreview={(entry) => void handleOpenFile(entry)}
              />
            </Box>
          </>
        ) : null}
      </Box>

      <DirectoryContextMenu
        entry={contextMenuEntry}
        anchorPosition={contextMenuPos}
        onClose={() => { setContextMenuEntry(null); setContextMenuPos(null); }}
        onOpen={(entry) => entry.isDir ? handleOpenDir(entry.name) : void handleOpenFile(entry)}
        onProperties={(entry) => {
          setPropertiesEntry(entry);
          setPropertiesFullPath(joinPath(currentPath, entry.name));
        }}
        selectedCount={selectedNames.size}
        onDownloadAll={() => { void handleDownloadAll(); }}
        onDownload={(entry) => {
          // Fix #8: Download… → trigger file_read + window.open URL artifact_id.
          // Reuse handleOpenFile dar deschide URL în tab nou cu download attribute.
          // Pentru moment: same flow ca preview, dar user poate face right-click "Save as".
          void handleOpenFile(entry);
        }}
      />

      {propertiesEntry ? (
        <PropertiesDialog
          open
          onClose={() => { setPropertiesEntry(null); setPropertiesFullPath(''); }}
          agentId={agentId}
          agentOnline={agentOnline}
          folderName={propertiesEntry.name}
          fullPath={propertiesFullPath}
          entry={propertiesEntry}
          onApplied={() => {
            // TODO Faza 4.4.4: persistă în localStorage + visual marker (deferred — cere
            // sync watchedPaths de la server, vine cu continuous-monitoring-fs).
          }}
        />
      ) : null}

      <FilePreviewPanel
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileName={previewName}
        fullPath={previewPath}
        loading={previewLoading}
        error={previewError}
        downloadUrl={previewDownloadUrl}
        mseSource={previewMseRef.current}
      />

      <Box data-role="file-explorer-statusbar" sx={{ px: 1, py: 0.25, borderTop: 1, borderColor: 'divider', backgroundColor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entries.length} {entries.length === 1 ? 'item' : 'items'}
          {selectedNames.size > 1 ? ` · ${selectedNames.size} selected` : (selectedFsEntry ? ` · ${formatSelectedSize(selectedFsEntry)}` : '')}
        </Typography>
        {/* Fix #3: ToggleButton selected vizibil pressed când treeOpen=true. */}
        <Tooltip title={treeOpen ? 'Hide folder tree' : 'Show folder tree'}>
          <ToggleButton
            value="tree"
            size="small"
            selected={treeOpen}
            onChange={() => setTreeOpen((o) => !o)}
            sx={{ py: 0.25, px: 0.5, border: 'none' }}
            aria-label="Toggle folder tree"
          >
            <AccountTreeIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
        {currentPath !== '' ? (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={viewMode}
            onChange={(_, v) => { if (v !== null) setViewMode(v as ViewMode); }}
            sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 0.5, border: 'none' } }}
          >
            <ToggleButton value="details" aria-label="Details">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="List">
              <ViewHeadlineIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="thumbnails" aria-label="Thumbnails">
              <ViewModuleIcon fontSize="small" />
            </ToggleButton>
            {/* Fix #5: Large thumbnails view mode în GroupButton. */}
            <ToggleButton value="largeThumbnails" aria-label="Large thumbnails">
              <ViewComfyIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        {/* Fix #2: ToggleButton selected vizibil pressed când previewPaneOpen=true. */}
        <Tooltip title={previewPaneOpen ? 'Hide preview pane' : 'Show preview pane'}>
          <ToggleButton
            value="preview"
            size="small"
            selected={previewPaneOpen}
            onChange={() => setPreviewPaneOpen((o) => !o)}
            sx={{ py: 0.25, px: 0.5, border: 'none' }}
            aria-label="Toggle preview pane"
          >
            <VisibilityOutlinedIcon fontSize="small" />
          </ToggleButton>
        </Tooltip>
      </Box>

      {/* Fix #4: Options modal. */}
      <FileExplorerOptionsDialog
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        value={optionsValue}
        onChange={handleOptionsChange}
      />

      {/* Bloc v1.0.10 #C — UNIFICAT: Options menu Properties + right-click folder Properties trec
          prin acelasi render `propertiesEntry`. State `propertiesForCurrent` driven setting `propertiesEntry`
          la entry sintetizat (currentFolderEntry) când e click din Options menu. */}

      {/* Bloc file-explorer-multiselect-download 2026-05-06: progress floating panel. */}
      <DownloadAllPanel
        open={bulkActive}
        progress={bulkProgress}
        onCancel={handleBulkCancel}
        onClose={handleBulkClose}
      />
    </Box>
  );
}
